// src/pages/Timesheet/useTimesheet.js
import { useEffect, useState } from "react";

const API_BASE = "https://suryojasvi-reflection-api.wyzmindz.com/api";

export default function useTimesheet() {
  const empID = localStorage.getItem("empID") || "";
  const email = localStorage.getItem("email") || "";
  const roleID = Number(localStorage.getItem("role") || "3");

  const today = new Date().toISOString().split("T")[0];

  const [date, setDate] = useState(today);
  const [tasks, setTasks] = useState([{ project: "", client: "", task: "", hours: "" }]);
  const [totalHours, setTotalHours] = useState(0);
  const [history, setHistory] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingDate, setEditingDate] = useState(null);

  const [filterProject, setFilterProject] = useState("");
  const [filterPeriod, setFilterPeriod] = useState("daily");
  const [filterValue, setFilterValue] = useState(today);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [downloadMonth, setDownloadMonth] = useState(new Date().toISOString().slice(0, 7));

  const [showMyTasksModal, setShowMyTasksModal] = useState(false);
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [loadingMyTasks, setLoadingMyTasks] = useState(false);
  const [canAddTimesheet, setCanAddTimesheet] = useState(false);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [loadingAssignHierarchy, setLoadingAssignHierarchy] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const [assignableEmployees, setAssignableEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");

  const [assignProject, setAssignProject] = useState("");
  const [assignClient, setAssignClient] = useState("");
  const [assignTaskText, setAssignTaskText] = useState("");
  const [assignHours, setAssignHours] = useState("");
  const [assignDueDate, setAssignDueDate] = useState("");

  const [loadingHistory, setLoadingHistory] = useState(false);

  /* SUCCESS POPUP */
  const [successPopup, setSuccessPopup] = useState(null); // { type, title, message }

  // ================= TOTAL HOURS =================
  useEffect(() => {
    const sum = tasks.reduce((acc, t) => acc + (parseFloat(t.hours) || 0), 0);
    setTotalHours(Number(sum.toFixed(1)));
  }, [tasks]);

  // ================= INITIAL LOAD =================
  useEffect(() => {
    loadHistory();
    loadAssignedTasks();
    setCanAddTimesheet(true); // ✅ Always allow — no role restriction on Add Timesheet button
    // ⭐ No getDefaultDate – so no dependency warning
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ================= WHEN ASSIGN MODAL OPENS =================
  useEffect(() => {
    if (showAssignModal && (roleID === 1 || roleID === 2)) {
      loadAssignableEmployees();
    }
  }, [showAssignModal]);

  useEffect(() => {
    if (!showAssignModal) setSelectedEmployee("");
  }, [showAssignModal]);

  // ================= HELPERS =================
  const mongoToDDMM = (m) => {
    try {
      const d = new Date(m);
      return `${String(d.getDate()).padStart(2, "0")}/${String(
        d.getMonth() + 1
      ).padStart(2, "0")}/${d.getFullYear()}`;
    } catch {
      return m || "";
    }
  };

  const getDefaultValue = (period) => {
    const now = new Date();
    if (period === "daily") return today;

    if (period === "weekly") {
      const year = now.getFullYear();
      const firstJan = new Date(year, 0, 1);
      const days = Math.floor((now - firstJan) / 86400000);
      const week = String(Math.ceil((days + firstJan.getDay() + 1) / 7)).padStart(2, "0");
      return `${year}-W${week}`;
    }

    if (period === "monthly")
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

    return today;
  };

  const getCalendarType = (p) =>
    p === "daily" ? "date" : p === "weekly" ? "week" : "month";

  const getWeekRange = (iso) => {
    if (!iso) return null;
    try {
      const [y, w] = iso.split("-W");
      const year = Number(y);
      const week = Number(w);
      const jan4 = new Date(year, 0, 4);
      const day = jan4.getDay() || 7;
      const monday = new Date(jan4);
      monday.setDate(jan4.getDate() - (day - 1));

      const start = new Date(monday);
      start.setDate(start.getDate() + (week - 1) * 7);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);

      const fmt = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(d.getDate()).padStart(2, "0")}`;

      return { from: fmt(start), to: fmt(end) };
    } catch {
      return null;
    }
  };

  // ================= TASK FORM =================
  const addTask = () =>
    setTasks((prev) => [...prev, { project: "", client: "", task: "", hours: "" }]);

  const removeTask = (i) =>
    setTasks((prev) =>
      prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i)
    );

  const updateTask = (i, field, val) =>
    setTasks((prev) =>
      prev.map((t, idx) => (idx === i ? { ...t, [field]: val } : t))
    );

  const blockSunday = (val) => {
    const d = new Date(val);
    if (d.getDay() === 0) return alert("Sunday cannot be selected.");
    setDate(val);
  };

  const handleAttachmentChange = (e) =>
    setAttachments(Array.from(e.target.files));

  // ================= CHECK PERMISSION =================
  // ✅ UPDATED: Employees can always add timesheets independently
  // Manager assignment is optional — not required to submit timesheet
  const checkTimesheetPermission = (_tasks) => {
    setCanAddTimesheet(true);
  };

  // ================= EDIT =================
  const startEditTimesheet = (entry) => {
    setIsEditing(true);
    setShowForm(true);

    const d = entry.SelectedDate.split("T")[0];
    setEditingDate(d);
    setDate(d);

    setTasks(
      (entry.tasks || []).map((t) => ({
        project: t.project || "",
        client: t.client || "",
        task: t.task || "",
        hours: t.hours || "",
      }))
    );
  };

  // ================= HISTORY =================
  const loadHistory = async (clearFilters = false) => {
    setLoadingHistory(true);
    try {
      const params = new URLSearchParams({ empID });

      if (!clearFilters) {
        if (filterProject.trim()) params.append("project", filterProject.trim());

        if (filterPeriod === "daily") params.append("date", filterValue);

        if (filterPeriod === "weekly") {
          const r = getWeekRange(filterValue);
          if (r) {
            params.append("fromDate", r.from);
            params.append("toDate", r.to);
          }
        }

        if (filterPeriod === "monthly") params.append("month", filterValue);
      }

      const res = await fetch(`${API_BASE}/timesheet/history?${params}`);
      const data = await res.json();
      setHistory(res.ok ? data.records || [] : []);
      setCurrentPage(1);
    } finally {
      setLoadingHistory(false);
    }
  };

  // ================= SUBMIT =================
  const submitTimesheet = async () => {
    for (const t of tasks) {
      if (!t.project.trim()) return alert("Please enter project");
      if (!t.client.trim()) return alert("Please enter client");
      if (!t.task.trim()) return alert("Please enter task");
      if (!t.hours || Number(t.hours) <= 0)
        return alert("Please enter valid hours");
    }

    const fd = new FormData();
    fd.append("empID", empID);
    fd.append("date", date);
    fd.append("tasks", JSON.stringify(tasks));
    fd.append("totalHours", totalHours);
    attachments.forEach((file) => fd.append("attachments", file));

    const res = await fetch(`${API_BASE}/timesheet/submit`, {
      method: "POST",
      body: fd,
    });

    const d = await res.json();
    if (!res.ok) return alert(d.message || "Submit failed");

    setSuccessPopup({ type: "success", title: "Timesheet Submitted!", message: "Your timesheet has been submitted successfully." });
    setShowForm(false);
    setTasks([{ project: "", client: "", task: "", hours: "" }]);
    setAttachments([]);
    loadHistory();
  };

  // ================= UPDATE =================
  const updateTimesheet = async () => {
    const fd = new FormData();
    fd.append("empID", empID);
    fd.append("date", editingDate);
    fd.append("tasks", JSON.stringify(tasks));
    fd.append("totalHours", totalHours);

    const res = await fetch(`${API_BASE}/timesheet/update`, {
      method: "PATCH",
      body: fd,
    });

    const data = await res.json();

if (!res.ok) {
  // ⭐ TIME LIMIT MESSAGE FROM BACKEND
  alert(
    data.detail ||
    data.message ||
    "Can't able to edit this timesheet now."
  );
  return;
}


    setSuccessPopup({ type: "success", title: "Timesheet Updated!", message: "Your timesheet has been updated successfully." });
    setIsEditing(false);
    setEditingDate(null);
    setShowForm(false);
    setTasks([{ project: "", client: "", task: "", hours: "" }]);
    loadHistory();
  };

  // ================= DELETE =================
  const deleteTimesheet = async (date) => {
    if (!window.confirm("Delete this timesheet?")) return;

    const res = await fetch(
      `${API_BASE}/timesheet/delete?empID=${empID}&date=${date}`,
      { method: "DELETE" }
    );

    const data = await res.json();

if (!res.ok) {
  alert(
    data.detail ||
    data.message ||
    "Can't able to delete this timesheet now."
  );
  return;
}


    setSuccessPopup({ type: "success", title: "Deleted!", message: "Timesheet entry deleted successfully." });
    loadHistory();
  };

  // ================= ASSIGNED TASKS =================
  const loadAssignedTasks = async () => {
    setLoadingMyTasks(true);

    const res = await fetch(`${API_BASE}/timesheet/my_tasks?empID=${empID}`);
    const d = await res.json();

    if (res.ok) {
      const tasks = d.tasks || [];

      // FILTER ❗ remove expired tasks
      const todayDate = new Date().toISOString().split("T")[0];
      const valid = tasks.filter((t) => !t.dueDate || todayDate <= t.dueDate);

      setAssignedTasks(valid);
      checkTimesheetPermission(valid);
    } else {
      setAssignedTasks([]);
      setCanAddTimesheet(true);
    }

    setLoadingMyTasks(false);
  };

  const autoFillTask = (t) => {
    setShowMyTasksModal(false);
    setShowForm(true);
    setTasks([
      {
        project: t.project || "",
        client: t.client || "",
        task: t.task || "",
        hours: "",
      },
    ]);
  };

  // ================= ASSIGN EMPLOYEES =================
  const loadAssignableEmployees = async () => {
    setLoadingAssignHierarchy(true);

    const res = await fetch(
      `${API_BASE}/dashboard/assignable_employees?email=${email}`
    );

    const d = await res.json();
    setAssignableEmployees(res.ok ? d.records || [] : []);
    setLoadingAssignHierarchy(false);
  };

  const submitAssignTask = async (e) => {
    e.preventDefault();

    if (!selectedEmployee) return alert("Select employee");
    if (!assignProject.trim()) return alert("Enter project");
    if (!assignClient.trim()) return alert("Enter client");
    if (!assignTaskText.trim()) return alert("Enter task");
    if (!assignHours || Number(assignHours) <= 0)
      return alert("Enter valid hours");
    if (!assignDueDate) return alert("Select due date");

    const fd = new FormData();
    fd.append("email", email);
    fd.append("assignedTo", selectedEmployee);
    fd.append("project", assignProject);
    fd.append("client", assignClient);
    fd.append("task", assignTaskText);
    fd.append("expectedHours", assignHours);
    fd.append("dueDate", assignDueDate);

    const res = await fetch(`${API_BASE}/timesheet/assign_task`, {
      method: "POST",
      body: fd,
    });

    const d = await res.json();
    if (!res.ok) return alert(d.message || "Assign failed");

    setSuccessPopup({ type: "success", title: "Task Assigned!", message: "Task has been assigned successfully." });

    setShowAssignModal(false);
    setAssignProject("");
    setAssignClient("");
    setAssignTaskText("");
    setAssignHours("");
    setAssignDueDate("");
    setSelectedEmployee("");
  };

  // ================= DOWNLOAD =================
  const downloadMonthlyTimesheet = async (month) => {
    if (!month) return alert("Select month");

    const url = `${API_BASE}/timesheet/download_monthly?email=${encodeURIComponent(
      email
    )}&empID=${encodeURIComponent(empID)}&month=${encodeURIComponent(month)}`;

    const res = await fetch(url);
    if (!res.ok) {
      const err = await res.json();
      return alert(err.message || "Download failed");
    }

    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `Timesheet_${empID}_${month}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // ================= PAGINATION =================
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = history.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(history.length / itemsPerPage);

  const goToPrev = () => setCurrentPage((p) => Math.max(1, p - 1));
  const goToNext = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  return {
    date,
    setDate,
    tasks,
    setTasks,
    totalHours,
    history,
    setHistory,
    showForm,
    setShowForm,
    attachments,
    setAttachments,
    isEditing,
    setIsEditing,
    editingDate,

    filterProject,
    setFilterProject,
    filterPeriod,
    setFilterPeriod,
    filterValue,
    setFilterValue,

    currentPage,
    currentItems,
    totalPages,
    goToPrev,
    goToNext,
    setCurrentPage,

    downloadMonth,
    setDownloadMonth,

    showMyTasksModal,
    setShowMyTasksModal,
    assignedTasks,
    loadingMyTasks,
    canAddTimesheet,

    showAssignModal,
    setShowAssignModal,
    loadingAssignHierarchy,
    showDownloadModal,
    setShowDownloadModal,

    assignableEmployees,
    selectedEmployee,
    setSelectedEmployee,
    assignProject,
    setAssignProject,
    assignClient,
    setAssignClient,
    assignTaskText,
    setAssignTaskText,
    assignHours,
    setAssignHours,
    assignDueDate,
    setAssignDueDate,

    loadingHistory,
    roleID,

    successPopup,
    setSuccessPopup,

    mongoToDDMM,
    getCalendarType,
    getDefaultValue,

    addTask,
    removeTask,
    updateTask,
    blockSunday,
    handleAttachmentChange,
    startEditTimesheet,
    loadHistory,
    submitTimesheet,
    updateTimesheet,
    deleteTimesheet,
    loadAssignedTasks,
    autoFillTask,
    loadAssignableEmployees,
    submitAssignTask,
    downloadMonthlyTimesheet,
  };
}
